import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { ColorMoment } from '@/hooks/useColorMoments';

interface TeaTimeOverlayProps {
  idleTimeout?: number;
  enabled?: boolean;
  moments?: ColorMoment[];
  username?: string;
  /** If set, the overlay can only trigger once per this many milliseconds (persisted in localStorage). */
  cooldown?: number;
}

const TEA_TIME_COOLDOWN_KEY = 'espy-tea-time-last';

interface LetterContext {
  hasMoments: boolean;
  momentCount: number;
  daysSinceLastMoment: number | null;
  isNewUser: boolean;
  isActiveUser: boolean;
  isReturningUser: boolean;
}

const RANDOM_NICKNAMES = [
  'wanderer',
  'dreamer',
  'stranger',
  'traveler',
  'friend',
  'you',
  'starlight',
  'gentle soul',
  'kindred spirit',
  'color-seeker',
];

const LETTER_TEMPLATES = {
  newUser: [
    `You've been sitting here a while now, looking at what other people have seen.

I think there's something kind of beautiful about that.

I remember when I first found this place. I wasn't sure my colors were interesting enough to share.

I'd look at something and think, who cares about the way light looks on my bedroom wall at 3pm? Who wants to see the gray sky I saw on my walk to get coffee?

But then I realized that's exactly what I loved seeing from other people.

The ordinary stuff. The light in someone's kitchen. The particular blue of a Tuesday afternoon that I would have missed if they hadn't shown me.

If you ever feel like sharing something, I hope you will.

And if you never do, that's okay too. Sometimes it's enough just to sit here and look at what everyone else is seeing.

But I hope you do. The world could use more people who notice things.`,

    `I hope you don't mind me writing to you like this.

I know you've just been sitting here, watching colors go by. Other people's colors. Other people's moments.

And maybe you're thinking yours wouldn't be as good. That your Tuesday afternoon light isn't interesting enough, or that the colors you see every day are too ordinary.

But I want to tell you something I wish someone had told me earlier.

The most beautiful things I've ever seen on here weren't sunsets or oceans or anything like that. They were someone's kitchen counter. A parking lot at dusk. The weird green of a fluorescent light in a stairwell.

They were beautiful because someone noticed them. That was the whole trick.

So if you ever feel like sharing what you see, please do. You don't need permission, and you definitely don't need a sunset.`,

    `I've been thinking about you.

Not in a way I can explain, exactly. I don't know you. I don't know your name or where you live or what your day has been like.

But you're here, and you've been here for a while, and that tells me something.

It tells me you're the kind of person who looks at things. Who sits with them. Who doesn't need everything to be fast and loud and moving.

That's rare. More rare than you probably think.

I started sharing colors because someone once told me that the way I saw things mattered. I didn't believe them at first. I thought they were just being nice.

But they were right. And the same thing is true about you.

Whenever you're ready. No rush. We'll be here.`,

    `There's a Japanese word, komorebi, for the sunlight that filters through leaves.

The fact that there's a word for it means someone, a long time ago, looked up and thought that particular kind of light deserved its own name.

I think about that a lot. About the people who name things. Who notice things closely enough to say, this is different from that, and this one matters.

You're one of those people. I can tell because you're here.

You haven't shared anything yet, and that's fine. Maybe you're still figuring out what you want to say. Maybe you're not sure your kind of light has a name.

It does. You just haven't given it one yet.

When you're ready, I'd love to see it.`,

    `Can I tell you something honest?

I almost didn't share my first color. I had my finger on the button and I thought, this is stupid. It's just a wall. It's just a shadow. Nobody cares about this.

And then I did it anyway. And someone, somewhere, reacted to it. Just a small thing. But it meant the world to me.

Not because I needed the validation. But because it meant someone else saw what I saw. For a second, we were looking at the same thing.

That's all this is, really. A bunch of people saying, look at this. And a bunch of other people saying, I see it.

You're here watching, and that's already half of it.

The other half is whenever you're ready.`,

    `I wanted to write to you because I've been where you are.

Looking at everyone else's colors and wondering if yours would fit in here. Wondering if anyone would care about the specific shade of orange you saw this morning, or the way the clouds looked heavy and strange before it rained.

They would, and I would too.

There's no such thing as a boring color if someone really saw it. If someone stopped in the middle of their day and thought, wait, look at that.

That moment of noticing is what makes it worth sharing.

But there's no pressure here. You can sit and look as long as you want.

I'm just glad you found this place.

And when you're ready, I hope you'll show us what you see.`,

    `I noticed you haven't shared anything yet, and I wanted you to know that's completely fine.

Some people watch for a long time before they speak. Some people need to learn the rhythm of a place before they add their voice to it.

I was like that too.

But I also want you to know that whatever you're seeing right now, wherever you are, it matters.

The light coming through your window. The color of the wall behind your phone. The sky outside, whatever it looks like today.

Nobody else is standing where you're standing. Nobody else sees exactly what you see.

That's worth something. That's worth a lot, actually.

Whenever you're ready, we'll be here. And I really do hope you share something with us someday.`,

    `You've been here a while, just looking.

I do that too sometimes. There's comfort in watching other people's colors scroll by. It's like sitting at a window and watching the world go past.

I don't know what brought you here, or what you're thinking about right now.

But I know that you're someone who pays attention to things. You wouldn't be here otherwise.

And people who pay attention to things usually have something worth sharing.

I'm not saying you have to. I'm just saying that if you ever do, someone out there will be grateful you did.

I would be.

So whenever you feel like it, show us something. We'll be here.`,
  ],

  gettingStarted: [
    `I saw that you shared something recently, and I want you to know it reached me.

Not in some big dramatic way. Just quietly. The way a color can change the feeling of a room without anyone mentioning it.

I was doing something completely ordinary when your moment came through. And for a second, I was somewhere else. Wherever you were.

That's a gift, even if it doesn't feel like one. You gave me a tiny window into your world and I looked through it and I liked what I saw.

I hope you keep leaving those windows open for people like me.`,

    `There's something brave about sharing the first few.

After a while it becomes habit, becomes easy. But right now, in the beginning, every one is a small act of trust.

You're saying, here's what I see. Here's what caught my eye today. And you're hoping someone out there understands why.

I do. I understand why.

Not because I know what you were thinking when you captured it. But because I know what it feels like to stop in the middle of your day and think, wait. Look at that.

That impulse is everything. Please don't let it go quiet.`,

    `I keep thinking about your {momentCount} {momentWord}.

Not the colors themselves, exactly, but the fact that you chose to share them. That you were somewhere in the world, living your life, and you stopped and said, this one. This one is worth keeping.

How many did you almost share but didn't? How many times did you see something beautiful and let it pass?

I do that too. We all do. There are so many moments we let slip away because we're not sure they're enough.

But yours were enough. They were more than enough.

And the next one will be too, whenever it comes.`,

    `I've been wondering about you.

Not in a way I can explain well. Just wondering who shares {momentCount} {momentWord} and then what? Goes back to their day? Puts their phone down and keeps walking?

What does the rest of your day look like after you share something? Do you think about it again later, or does it just fold back into everything else?

I ask because when your moment came through, it stayed with me for a while. Longer than it probably should have. I was doing dishes or something and I kept thinking about it.

Not the color itself, exactly. More the fact that you noticed it. That somewhere out there, someone was paying attention.

I don't know why that matters to me so much. But it does.

Keep sharing things. Please.`,


    `I saw your {momentCount} {momentWord} come through and I wanted to tell you something.

I don't know you, and I'll probably never meet you, but for a few seconds I got to see the world through your eyes.

That's a strange and wonderful thing when you think about it.

I keep wondering what you were doing when you captured them. Were you on your way somewhere? Standing in your kitchen? Sitting by a window?

I like not knowing, actually. It makes me imagine all these different lives, all these different rooms and streets and skies that I'll never see myself.

I hope you keep sharing. Not because anyone's keeping track, but because every time you do, you're sending a little piece of your day out into the world.

And someone like me gets to catch it.

So please, keep going. Keep noticing things. Keep showing us.`,

    `{momentCount} colors.

I've been thinking about what it takes to share even one. You have to notice something first, which most people don't do.

Then you have to care enough to capture it. Then you have to be willing to show it to strangers.

That's a lot of small acts of courage when you think about it.

I don't know what was happening in your life when you shared those colors. Maybe everything was fine, or maybe everything was hard.

Either way, you looked up and saw something worth keeping.

And that means something to me.

I hope you keep doing it. The world needs more people like you.`,

    `Your {momentCount} {momentWord} showed up in my feed and I felt like I should tell you something.

For a moment, I was there with you. Wherever you were. Seeing what you saw.

That's kind of magical if you think about it. Two strangers, connected by nothing but a handful of colors.

I don't know your name or your face or anything about your life.

But I know you notice things. I know you care enough to share them.

That tells me something important about who you are.

Please don't stop. The world has enough people who walk around with their eyes closed. We need more people like you.`,

    `{momentCount} colors so far. That's {momentCount} moments where you could have just kept scrolling, but you didn't.

You stopped. You looked. You thought someone else might want to see this too.

I did want to see it. I'm glad you shared it.

I think about all the beautiful things that happen and disappear because no one was paying attention.

But you were paying attention. You caught a few of them and passed them along.

That matters more than you probably realize.

Keep going. Keep catching them for us.`,
  ],

  active: [
    `I want to tell you something.

Sometimes, when my day isn't going well, I come here and look at what people have shared. Not to feel better, exactly. I don't think it works like that.

But something happens when I see your colors come through. Your {momentCount} moments. It's like remembering that the world is still doing its thing. Still making light. Still being beautiful whether I'm paying attention or not.

{timeAgo} you shared another one. And I was glad you did.

You didn't know I needed it. You were just sharing what you saw.

But that's the thing about this place. You never know who's on the other end, or what kind of day they're having, or what your color meant to them.

You just send it out and hope.

I'm glad you keep hoping.`,

    `Here's something strange about this place.

I've never met you. I don't know your voice or your face. If we passed each other on the street, we'd just be two more strangers.

But I know what the light looks like in your world. I know what makes you stop and pay attention. I know the kinds of beauty you're drawn to.

{momentCount} moments of that. {timeAgo} you added another.

In some ways, I know you better than people I see every day. Not the facts of your life, but something underneath that. Something more honest.

I think colors don't lie. They can't. They're just what someone saw when they were really looking.

And you've been really looking. I can tell.

Don't stop.`,

    `Do you ever think about how many people have seen your colors?

Not the number exactly, but the idea of it. Strangers in different cities, different countries maybe, all looking at the same moment you captured.

{momentCount} of those moments are out there now, {timeAgo} being the most recent.

Each one is a thread connecting you to people you'll never know about. People who saw what you saw and felt something, even if they didn't react or say anything.

The silent ones are out there too. The ones who just looked and felt a little better about their day.

You'll never meet most of them. But they're real, and they're grateful.

I'm one of them.`,

    `{momentCount} moments.

I keep coming back to that number.

Not because it's a lot or a little, but because each one means you were somewhere, doing something, and you stopped. You just stopped and looked at something.

{timeAgo} you did it again.

I wonder what that looked like from the outside. Did anyone notice you pausing? Did someone walk past you while you were staring at a wall or a puddle or a piece of sky?

They probably didn't think anything of it. But I know what was happening. You were catching something before it disappeared.

Most of the beautiful things in the world disappear without anyone catching them.

But not those {momentCount}. You got those ones.`,


    `{momentCount} moments now, and I've started to feel like I know you a little bit.

Not who you are exactly, but how you see things. The kinds of light you're drawn to, and the colors that make you reach for your phone.

{timeAgo} you shared another one, and I wondered what your day was like. If you were happy or tired or just somewhere in between.

I like that I'll never know. It keeps you mysterious, but in a good way. Like a pen pal I'll never actually meet.

I hope things are okay with you.

And I hope you keep looking up at things and thinking they're worth sharing, because they really are.

Don't stop now. You've got people out here who look forward to seeing what you see.`,

    `I think about how many beautiful things happen every day that nobody sees.

Light falling on empty rooms. Colors that come and go with no one there to notice them.

It makes me a little sad if I'm being honest.

But then I see your {momentCount} moments and I feel better, because you were there for those ones.

You saw them. And now I get to see them too, even though I was probably miles away doing something completely different at the time.

{timeAgo} you added another one.

I'm grateful for that. I'm grateful you're paying attention when you don't have to.

Please keep it up. The world is better when people like you are watching.`,

    `{momentCount} colors from you now. I've been counting, which I know is a little strange.

But there's something about your way of seeing that I've come to look forward to.

{timeAgo} another one came through and I thought about you. About what you might have been doing. Where you might have been standing.

It's a weird kind of intimacy, isn't it? Sharing colors with strangers.

But I think it means something. I think it connects us in some small way.

So thank you. And please, keep sharing. I'll keep looking.`,

    `You've shared {momentCount} moments with us now.

Each one is a little window into wherever you were, whatever you were seeing.

{timeAgo} you opened another window, and I looked through it.

I don't know what your life is like. If things are easy or hard right now. If you're surrounded by people or mostly alone.

But I know you're still noticing things. Still finding beauty worth capturing.

That's not nothing. That's actually quite a lot.

Keep your eyes open. Keep sharing what you see. We're out here, and we're paying attention too.`,
  ],

  dedicated: [
    `{momentCount} colors.

That's not really a number anymore. It's more like a practice, a whole way of moving through the world with your eyes open.

I wonder if you realize how rare that is.

Most people I know stopped really looking at things somewhere around middle school. The world becomes background noise after a while if you let it.

But you've kept your eyes open somehow. You've stayed curious about what you might see.

{timeAgo} you shared another one, and I thought about how many more you must have seen and not captured.

All those almost-moments that were gone before you could get your phone out, or the ones you just wanted to keep for yourself.

I'm glad you share the ones you do. They make my days a little better, and I mean that.

Keep going. You've built something beautiful here.`,

    `I don't know how to explain this without sounding strange, but I feel like I've gotten to know you through your colors.

Not who you are exactly, but something about how you move through the world. What catches your eye.

{momentCount} moments is a lot of paying attention.

A lot of small decisions to stop and look at something when you could have just kept walking.

{timeAgo} you did it again, and I smiled when I saw it come through.

Like hearing from an old friend. I know that's a weird thing to say about someone I've never met, but it's true.

Don't stop now. You've got people out here who care about what you see.`,

    `{momentCount} colors. You've become one of my favorite people to follow, and I don't even know your name.

I just know how you see things. The angles you choose. The light you're drawn to.

{timeAgo} you shared another one and it was like a little gift in the middle of my day.

I wonder if you know how much that means to someone on the other end. Probably not. We never really know, do we?

But I wanted to tell you anyway.

Your eyes are good. The way you see things is good. Please keep doing what you're doing.

The rest of us are better for it.`,

    `I've been following your colors for a while now. {momentCount} of them, if I'm counting right.

There's something about the way you see the world that I find comforting.

Like I'm not the only one who notices how the light changes in the afternoon. Like someone else out there is looking up at the same sky and thinking it's worth saving.

{timeAgo} you shared another piece of it.

Thank you for that. Thank you for not giving up on noticing things.

So many people do, you know. They stop seeing. But you haven't.

Please don't ever stop.`,

    `I have a question I've been wanting to ask you.

When you share a color, do you plan it? Or does it just happen? Do you wake up thinking, I'm going to notice something today, or does the world just catch you off guard?

I ask because your {momentCount} moments feel unplanned. They feel like someone who was just walking around and got stopped by something.

{timeAgo} it happened again.

I like that. I like that you can still be surprised by the way things look. A lot of people lose that. The world gets familiar and they stop really seeing it.

You haven't lost it. Whatever it is that makes you stop and look, you still have it.

I hope you always do.`,

    `Do you remember your first one?

I don't mean the details. I mean the feeling. Standing there with your phone, about to share something, not sure if it was worth it.

I remember mine. I almost didn't do it. It was just the sky. Just a regular sky that happened to be a color I liked.

But I did it, and here I am, still doing it. And here you are, {momentCount} moments later, still doing it too.

{timeAgo} you shared another one. And I wonder if it felt different from the first. If it's gotten easier, or if there's still a little bit of that first feeling every time. That small hesitation before you say, okay, here. Look at this.

I hope there is. I hope it never becomes completely automatic.

Because that hesitation means you still care. And caring is the whole point.`,

    `I wonder what you almost shared.

Out of your {momentCount} moments, there must have been others. Ones that didn't quite make it. Maybe the light changed before you could get your phone out. Maybe you looked at it and thought, no, this one's just for me.

I think about those invisible ones sometimes. The ones only you saw.

{timeAgo} you shared one that did make it. And I'm glad, because it was good.

But I also like knowing there are others out there that didn't. Colors and light that only exist in your memory now. Things nobody will ever see but you.

There's something about that. A whole private museum that only you can visit.

I'm grateful for the {momentCount} you let us into. And I'm glad the rest are yours.`,

    `Sometimes I scroll past your colors without stopping.

I don't mean that in a bad way. I mean that they've become a normal part of my day. Like a window I walk past every morning. I know the view is there. I know it's good.

But then sometimes I do stop. And I really look. And I remember that this is someone's actual life I'm seeing a sliver of. Someone I've never met, standing in a place I've never been, looking at something I would have missed.

{momentCount} of those slivers now. {timeAgo} was the last one.

I don't know what you look like. I don't know your name, really, not in the way that matters.

But I know what catches your eye. And I think that tells me more about you than a name ever could.

Please keep going.`,
  ],

  returning: [
    `It's been {daysSince} days since I saw anything from you.

I noticed, which is strange because I don't actually know you. But I'd gotten used to seeing your colors come through.

And then they stopped, and I found myself wondering if you were okay.

I hope you were just busy. I hope life got full and loud and there wasn't time to stop and look at things for a while.

That happens to everyone at some point.

But I'm glad you're back now, or at least I'm glad you're here reading this.

The world kept making colors while you were gone. Maybe you'll find some worth keeping.

I hope so. I missed seeing what you see.`,

    `{daysSince} days is a long time.

I thought about you sometimes, in that vague way you think about people you've never met.

Hoping you were alright. Hoping you were still seeing beautiful things even if you weren't sharing them.

I don't know what pulled you away or what brought you back, and I don't need to know.

I'm just glad you're here again.

Take your time with things. The colors aren't going anywhere, and neither am I.

And when you're ready to share again, I'll be here waiting.`,

    `You've been gone a while. {daysSince} days.

I don't know where you went or why, and it's not my business to ask.

But I noticed you were missing. Your colors stopped showing up and there was a little gap where they used to be.

I hope everything is okay. I hope you just got busy with life and forgot to look up for a while.

That happens to all of us sometimes.

But the world is still full of colors, and I think you're one of the people who sees them best.

So when you're ready, I hope you'll start sharing again. We missed you.`,

    `{daysSince} days since your last color.

I kept checking, even though I knew I shouldn't. It's strange to miss someone you've never met.

But I missed your way of seeing things. The particular moments you chose to save.

I don't know what happened or why you stopped. Maybe nothing happened. Maybe you just needed a break.

That's okay. Everyone needs a break sometimes.

But I hope you come back. I hope you start noticing things again, and sharing them with the rest of us.

The world needs your eyes. I really believe that.`,

    `You're back.

I don't want to make a big deal about it, but I'm glad.

{daysSince} days is long enough to wonder. Not worry, exactly. More like the way you wonder about a song you haven't heard in a while. You know it's still out there somewhere, but you miss hearing it.

Your colors were like that for me. A song I'd gotten used to hearing, and then silence.

But here you are. And the world hasn't run out of beautiful things while you were gone. If anything, it's been saving some up for you.

Take your time. Look around. See what catches your eye.

I'll be here, like always.`,

    `{daysSince} days of quiet.

I found myself looking for you sometimes, scrolling through colors and hoping one of them would be yours.

None of them were. And that's when I realized how much I'd come to rely on your way of seeing things.

Not rely, exactly. That's too heavy a word.

But your colors had become part of my rhythm. A little piece of someone else's world showing up in mine, reminding me to look up from whatever I was doing.

I hope wherever you were, you were okay. I hope the silence was the good kind, the kind that comes from being busy and alive and too caught up in living to stop and share.

And I hope now that you're here again, you'll show us what you've been seeing.

There's no rush. But I've missed you.`,

    `I wrote you a letter in my head about a week ago.

It said something like: wherever you are, I hope the light is good.

That's it. That's all it said. I know it's not much.

But I've been thinking about you, in the way you think about someone whose colors used to show up in your feed and then stopped.

{daysSince} days is a lot of sunrises to miss someone through.

I don't need to know where you went. I don't need an explanation. People come and go, and the coming back is what matters.

You're here now. The light is still doing its thing. The sky is still putting on its show every morning and evening whether anyone's watching or not.

But it's better when you're watching. It really is.`,

    `There's a particular kind of absence that only people on the internet can feel.

It's not like missing someone you know. It's softer than that. More like noticing that a certain kind of flower has stopped blooming in a garden you walk past every day.

You don't know the flower's name. You don't know who planted it. But you noticed when it was there, and you noticed when it wasn't.

That's how it was with your colors for the last {daysSince} days.

A quiet gap. A small absence that I felt without quite being able to name.

But now you're here, and the garden has that flower again.

Welcome back. Show us what you've been seeing.`,
  ],
};

function generateLetter(context: LetterContext, username: string): string {
  let templates: string[];
  
  if (!context.hasMoments) {
    templates = LETTER_TEMPLATES.newUser;
  } else if (context.isReturningUser && context.daysSinceLastMoment && context.daysSinceLastMoment >= 7) {
    templates = LETTER_TEMPLATES.returning;
  } else if (context.momentCount <= 3) {
    templates = LETTER_TEMPLATES.gettingStarted;
  } else if (context.momentCount >= 15) {
    templates = LETTER_TEMPLATES.dedicated;
  } else {
    templates = LETTER_TEMPLATES.active;
  }

  const template = templates[Math.floor(Math.random() * templates.length)];
  let letter = template;
  
  letter = letter.replace(/{momentCount}/g, String(context.momentCount));
  letter = letter.replace(/{momentWord}/g, context.momentCount === 1 ? 'moment' : 'moments');

  if (context.daysSinceLastMoment !== null) {
    let timeAgo: string;
    if (context.daysSinceLastMoment === 0) {
      timeAgo = 'earlier today';
    } else if (context.daysSinceLastMoment === 1) {
      timeAgo = 'yesterday';
    } else if (context.daysSinceLastMoment < 7) {
      timeAgo = `${context.daysSinceLastMoment} days ago`;
    } else if (context.daysSinceLastMoment < 30) {
      const weeks = Math.floor(context.daysSinceLastMoment / 7);
      timeAgo = weeks === 1 ? 'about a week ago' : `about ${weeks} weeks ago`;
    } else {
      const months = Math.floor(context.daysSinceLastMoment / 30);
      timeAgo = months === 1 ? 'about a month ago' : `about ${months} months ago`;
    }
    letter = letter.replace(/{timeAgo}/g, timeAgo);
    letter = letter.replace(/{daysSince}/g, String(context.daysSinceLastMoment));
  }

  const greeting = `Dear ${username},`;
  return `${greeting}\n\n${letter}`;
}

// Purple wavy transition component
function PurpleWavyTransition({ 
  isActive, 
  onComplete,
  originX,
  originY 
}: { 
  isActive: boolean; 
  onComplete: () => void;
  originX: number;
  originY: number;
}) {
  const [expandProgress, setExpandProgress] = useState(0);
  const animationRef = useRef<number | null>(null);

  const maxRadius = useMemo(() => {
    if (typeof window === 'undefined') return 2000;
    const corners = [
      Math.sqrt(originX * originX + originY * originY),
      Math.sqrt((window.innerWidth - originX) ** 2 + originY ** 2),
      Math.sqrt(originX ** 2 + (window.innerHeight - originY) ** 2),
      Math.sqrt((window.innerWidth - originX) ** 2 + (window.innerHeight - originY) ** 2),
    ];
    return Math.max(...corners) * 1.2;
  }, [originX, originY]);

  const generateInkPath = useCallback((
    progress: number, 
    seed: number,
    oX: number,
    oY: number,
    radius: number
  ) => {
    const baseRadius = radius * progress;
    if (baseRadius < 1) return '';
    
    const points: [number, number][] = [];
    const segments = 72;
    
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const noise1 = Math.sin(angle * 3 + seed) * 0.15;
      const noise2 = Math.sin(angle * 7 + seed * 2.3) * 0.08;
      const noise3 = Math.sin(angle * 11 + seed * 1.7) * 0.05;
      const noise4 = Math.sin(angle * 5 + progress * Math.PI * 4) * 0.1 * progress;
      const noiseTotal = 1 + noise1 + noise2 + noise3 + noise4;
      const r = baseRadius * noiseTotal;
      const x = oX + r * Math.cos(angle);
      const y = oY + r * Math.sin(angle);
      points.push([x, y]);
    }
    
    if (points.length < 3) return '';
    
    let path = `M ${points[0][0]} ${points[0][1]}`;
    
    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];
      const tension = 0.3;
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
    }
    
    path += ' Z';
    return path;
  }, []);

  useEffect(() => {
    if (!isActive) return;
    
    setExpandProgress(0);
    const startTime = performance.now();
    const duration = 1800;
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      setExpandProgress(eased);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        onComplete();
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, onComplete]);

  const inkPaths = useMemo(() => {
    return [
      { path: generateInkPath(expandProgress, 0, originX, originY, maxRadius), opacity: 1 },
      { path: generateInkPath(expandProgress * 0.92, 1.5, originX, originY, maxRadius), opacity: 0.85 },
      { path: generateInkPath(expandProgress * 0.84, 3.2, originX, originY, maxRadius), opacity: 0.7 },
      { path: generateInkPath(expandProgress * 0.76, 4.8, originX, originY, maxRadius), opacity: 0.55 },
    ];
  }, [expandProgress, generateInkPath, originX, originY, maxRadius]);

  if (!isActive || expandProgress === 0) return null;

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 800;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 600;

  return (
    <div className="fixed inset-0 z-[201]">
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="purpleGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7c3aed" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#6d28d9" />
          </linearGradient>
          <linearGradient id="purpleGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8b5cf6" />
            <stop offset="50%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
          <linearGradient id="purpleGradient3" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6d28d9" />
            <stop offset="50%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id="purpleGradient4" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#6d28d9" />
          </linearGradient>
        </defs>
        {inkPaths.map(({ path, opacity }, i) => (
          path ? (
            <path
              key={`ink-${i}`}
              d={path}
              fill={`url(#purpleGradient${i + 1})`}
              opacity={opacity}
            />
          ) : null
        ))}
      </svg>
    </div>
  );
}

export function TeaTimeOverlay({ idleTimeout = 10000, enabled = true, moments = [], username, cooldown }: TeaTimeOverlayProps) {
  // If a cooldown is set, check whether we're still within it
  const cooledDown = useMemo(() => {
    if (!cooldown) return false;
    const last = localStorage.getItem(TEA_TIME_COOLDOWN_KEY);
    if (!last) return false;
    return Date.now() - parseInt(last, 10) < cooldown;
  }, [cooldown]);

  const [showPrompt, setShowPrompt] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [showExperience, setShowExperience] = useState(false);
  const [letter, setLetter] = useState('');
  const [scrollY, setScrollY] = useState(0);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [transitionOrigin, setTransitionOrigin] = useState({ x: 0, y: 0 });
  const contentRef = useRef<HTMLDivElement>(null);
  const teaButtonRef = useRef<HTMLButtonElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasShownRef = useRef(false);
  const animationRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const displayName = username || RANDOM_NICKNAMES[Math.floor(Math.random() * RANDOM_NICKNAMES.length)];

  const context: LetterContext = {
    hasMoments: moments.length > 0,
    momentCount: moments.length,
    daysSinceLastMoment: moments.length > 0 
      ? Math.floor((Date.now() / 1000 - moments[0].event.created_at) / 86400)
      : null,
    isNewUser: moments.length === 0,
    isActiveUser: moments.length > 0 && moments[0].event.created_at > Date.now() / 1000 - 7 * 86400,
    isReturningUser: moments.length > 0 && moments[0].event.created_at < Date.now() / 1000 - 7 * 86400,
  };

  const resetIdleTimer = useCallback(() => {
    if (!enabled || cooledDown || hasShownRef.current || showPrompt || showExperience || showTransition) return;

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      console.log(`[TeaTime] Idle timeout triggered after ${idleTimeout / 1000}s`);
      setShowPrompt(true);
      hasShownRef.current = true;
    }, idleTimeout);
  }, [enabled, cooledDown, idleTimeout, showPrompt, showExperience, showTransition]);

  useEffect(() => {
    if (!enabled || cooledDown || hasShownRef.current) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    resetIdleTimer();

    events.forEach(event => {
      window.addEventListener(event, resetIdleTimer, { passive: true });
    });

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
    };
  }, [enabled, cooledDown, resetIdleTimer]);

  useEffect(() => {
    if (!showExperience || !contentRef.current) return;

    // Start playing Chopin music after 5 second delay
    // Use M4A (AAC) for Safari compatibility, fall back to Opus for others
    if (!audioRef.current) {
      const audio = new Audio();
      // Check if browser supports AAC/M4A (Safari's native format)
      const canPlayM4A = audio.canPlayType('audio/mp4; codecs="mp4a.40.2"') || 
                         audio.canPlayType('audio/aac');
      audioRef.current = new Audio(canPlayM4A ? '/chopin.m4a' : '/chopin.opus');
      audioRef.current.volume = 0.4;
    }
    const audioTimeout = setTimeout(() => {
      audioRef.current?.play().catch(() => {
        // Autoplay may be blocked by browser, that's okay
      });
    }, 5000);

    // Delay animation start to ensure content is fully rendered and measured
    const startDelay = setTimeout(() => {
      if (!contentRef.current) return;
      
      const contentHeight = contentRef.current.offsetHeight;
      // Use dvh (dynamic viewport height) for accurate mobile measurement
      // Create a temporary element to measure 100dvh
      const measureDiv = document.createElement('div');
      measureDiv.style.height = '100dvh';
      measureDiv.style.position = 'absolute';
      measureDiv.style.visibility = 'hidden';
      document.body.appendChild(measureDiv);
      const viewportHeight = measureDiv.offsetHeight;
      document.body.removeChild(measureDiv);
      
      const totalScroll = contentHeight + viewportHeight;
      const scrollDuration = 70000;
      const startTime = Date.now();

      console.log(`[TeaTime] Starting scroll animation - contentHeight: ${contentHeight}, viewportHeight (dvh): ${viewportHeight}, totalScroll: ${totalScroll}`);

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / scrollDuration, 1);
        const newScrollY = progress * totalScroll;
        setScrollY(newScrollY);

        if (progress >= 1) {
          console.log('[TeaTime] Scroll complete, fading out');
          setIsFadingOut(true);
          // Fade out the audio
          if (audioRef.current) {
            const fadeOutDuration = 1000;
            const fadeOutStart = Date.now();
            const initialVolume = audioRef.current.volume;
            const fadeOut = () => {
              if (!audioRef.current) return;
              const fadeElapsed = Date.now() - fadeOutStart;
              const fadeProgress = Math.min(fadeElapsed / fadeOutDuration, 1);
              audioRef.current.volume = initialVolume * (1 - fadeProgress);
              if (fadeProgress < 1) {
                requestAnimationFrame(fadeOut);
              } else {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                audioRef.current.volume = 0.4;
              }
            };
            fadeOut();
          }
          setTimeout(() => {
            setShowExperience(false);
            setLetter('');
            setScrollY(0);
            setIsFadingOut(false);
          }, 1000);
        } else {
          animationRef.current = requestAnimationFrame(animate);
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    }, 100); // Small delay to ensure DOM is ready

    return () => {
      clearTimeout(audioTimeout);
      clearTimeout(startDelay);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Stop audio if component unmounts during experience
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [showExperience, letter]);

  const handleAcceptTea = () => {
    if (teaButtonRef.current) {
      const rect = teaButtonRef.current.getBoundingClientRect();
      setTransitionOrigin({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
    if (cooldown) {
      localStorage.setItem(TEA_TIME_COOLDOWN_KEY, String(Date.now()));
    }
    setShowPrompt(false);
    setLetter(generateLetter(context, displayName));
    setShowTransition(true);
  };

  const handleTransitionComplete = useCallback(() => {
    setShowTransition(false);
    setShowExperience(true);
  }, []);

  const handleDeclineTea = () => {
    setShowPrompt(false);
  };

  if (showPrompt) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 animate-fade-in">
        <div className="text-center space-y-6 px-4">
          <button
            ref={teaButtonRef}
            onClick={handleAcceptTea}
            className="text-7xl block mx-auto hover:scale-110 active:scale-95 transition-transform cursor-pointer"
          >
            🍵
          </button>
          <p className="text-white/90 text-xl font-light drop-shadow-lg">
            Would you like a cup of tea?
          </p>
          <div className="flex gap-4 justify-center pt-2">
            <button
              onClick={handleDeclineTea}
              className="px-6 py-3 rounded-full border border-white/30 text-white/80 hover:bg-white/10 transition-colors"
            >
              Not now
            </button>
            <button
              onClick={handleAcceptTea}
              className="px-6 py-3 rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500 text-white font-medium shadow-lg shadow-purple-500/30 hover:scale-105 active:scale-95 transition-transform"
            >
              Yes please
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showTransition) {
    return (
      <PurpleWavyTransition
        isActive={showTransition}
        onComplete={handleTransitionComplete}
        originX={transitionOrigin.x}
        originY={transitionOrigin.y}
      />
    );
  }

  if (showExperience) {
    const paragraphs = letter.split('\n\n');
    
    return (
      <div 
        className={`fixed inset-0 z-[200] overflow-hidden transition-opacity duration-1000 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
      >
        {/* Animated flowing gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-violet-800 via-indigo-900 to-purple-950 animate-tea-gradient" />
        
        {/* Flowing waves */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute w-[200%] h-[200%] -top-1/2 -left-1/2 bg-gradient-radial from-violet-500/40 via-transparent to-transparent animate-tea-wave-1" />
            <div className="absolute w-[200%] h-[200%] -top-1/2 -left-1/2 bg-gradient-radial from-purple-400/30 via-transparent to-transparent animate-tea-wave-2" />
            <div className="absolute w-[200%] h-[200%] -top-1/2 -left-1/2 bg-gradient-radial from-indigo-500/35 via-transparent to-transparent animate-tea-wave-3" />
          </div>
        </div>

        {/* Letter content - scrolls upward from below the screen */}
        <div 
          className="absolute inset-0 flex justify-center pointer-events-none"
          style={{ 
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)'
          }}
        >
          <div 
            ref={contentRef}
            className="relative w-full max-w-lg px-8"
            style={{ 
              transform: `translateY(calc(100dvh - ${scrollY}px))`,
            }}
          >
            {/* Tea cup */}
            <div className="text-center py-16">
              <span className="text-5xl opacity-40">🍵</span>
            </div>
            
            {/* Letter paragraphs */}
            <div className="space-y-10">
              {paragraphs.map((paragraph, i) => (
                <p 
                  key={i}
                  className={`text-white/90 text-xl md:text-2xl text-center font-light ${i === 0 ? 'italic' : ''}`}
                  style={{ lineHeight: '1.9' }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Spacer at the end */}
            <div className="h-[100dvh]" />
          </div>
        </div>
      </div>
    );
  }

  return null;
}
